import { Metadata } from "src/app/hickory-api";
import { AuthZAccessControl } from "../../../authentication/access-control";
import { AuthService } from "../../../authentication/auth.service";
import { AuthorizationViewModel, GenericAuthzCollection } from '../shared-authz-models';
import { AuthzConstants } from "../helpers/authz.constants";

export class EveryoneAuthZCollection extends GenericAuthzCollection {

  private readCheckedOrganically = false;

  override get can() { return AuthZAccessControl.canAccessAuthZType(this.auth, 'everyone'); }
  override canDeleteItems = false;

  constructor(
    private auth: AuthService, 
    metadata: Metadata, 
    actions: string[]
  ) {
    super(
      parseInt(metadata.tenantId) || auth.user.snapshot.tenantId,
      actions, 'Organization', 'public'
    );
    this.setItemsFromMetadata(metadata);
  }

  public hasAction(action: string) {
    return this.every(x => x.hasAction(action));
  }

  public override getUpdatedMetadata(metadata: Metadata): Metadata {
    const updated = { ...metadata };
    for (const action of this.actions) {
      updated.tags[this.getTagKey(action)] = this.every(x => x.hasAction(action)) ? ['allow'] : [];
    }
    return updated;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public override isActionReadonly(item: AuthorizationViewModel, action: string): boolean {
    if(action !== this.readActionName) return false;
    return this.hasAdditionalActions(item, this.readActionName);
  }

  public override setItemsFromMetadata(metadata: Metadata): void {
    const everyone = new AuthorizationViewModel('Everyone');
    for (const action of this.actions) {
      if (AuthzConstants.isEveryoneActionEnabled(action, metadata)) {
        everyone.grantAction(action);
        if(action === this.readActionName) {
          this.readCheckedOrganically = true;
        }
      }
    }
    this.setItems([everyone]);
  }

  public toggleEveryoneActionAndGetMetadata(model: AuthorizationViewModel, action: string, checked: boolean, metadata: Metadata, ...collections: GenericAuthzCollection[]) {

    this.handleActionToggle(model, action, checked);

    let updated = { ...metadata, ...this.getUpdatedMetadata(metadata) };
    for (const collection of collections) {
      updated = {
        ...updated,
        ...collection
          .toggleActionForAllItems(action, checked)
          .getUpdatedMetadata(metadata)
      }
    }
    return updated;
  }

  private handleActionToggle(model: AuthorizationViewModel, action: string, checked: boolean) {
    
    // track if the user checked/unchecked the box on their own
    if (action === this.readActionName) {
      this.readCheckedOrganically = checked;
    }

    // respects the user actively clicking read for everyone
    // when determining whether to uncheck the box when toggling another action
    if (action !== this.readActionName && (!model.hasAction(this.readActionName) || !this.readCheckedOrganically)) {
      if (checked || this.hasAdditionalActions(model, action, this.readActionName)) {
        model.grantAction(this.readActionName);
        this.readCheckedOrganically = false;
      } else {
        model.revokeAction(this.readActionName);
      }
    }

    // toggle the target action
    this.toggleItemAction(model, action);
  }

  private hasAdditionalActions(item: AuthorizationViewModel, ...actions: string[]) {
    return item.actions?.filter(x => !actions.includes(x))?.length > 0;
  }

  protected override getTagKey(action: string): string {
    return AuthzConstants.getEveryoneActionKey(action);
  }
}